import { IssueType, Severity } from '../../graphql';
import { getMentionTextParts } from '../../utils/mentionsHelper';
import { stripFormattingMarkers } from '../../utils/messageFormatting';

/**
 * Severity is what the author picks; `type` is derived from it, never chosen
 * separately. Every write path that creates an Issue from a person's own post
 * — `NewIssue`'s report form and the asset chat composer — must agree on this
 * mapping, or the same choice would produce two different records depending on
 * which surface it was made from. Lifted out of `newIssue.tsx`, where both
 * this and `extractTitleFromMessage` were declared inside the submit callback
 * and so could not be shared.
 */
export const getIssueTypeFromSeverity = (severity: Severity): IssueType => {
  switch (severity) {
    case Severity.Red:
    case Severity.Yellow:
    case Severity.Green:
      return IssueType.Report;
    case Severity.Purple:
      return IssueType.Note;
    default:
      return IssueType.Report;
  }
};

/**
 * Fallback title for a post whose author wrote no title: the first four words
 * of the message. A mention counts as one word — splitting `@[Jane Doe](uuid)`
 * on spaces would spend the whole budget on one name and leak the id into the
 * title.
 *
 * Formatting markers are stripped first. A title is rendered as plain text
 * everywhere it appears — the activity list, a thread header, a compact feed
 * row — so a title derived from the raw text would show `**bold**` in all of
 * them, which is what happened once the web composer could author markers.
 */
export const extractTitleFromMessage = (message: string): string => {
  if (!message) return '';

  const parts = getMentionTextParts(message);
  const tokens: string[] = [];

  parts.forEach((part) => {
    if (part.isMention) {
      tokens.push(part.text);
    } else {
      const plain = stripFormattingMarkers(part.text);
      tokens.push(...plain.split(' ').filter((w) => w.trim()));
    }
  });

  return tokens.slice(0, 4).join(' ');
};
